import React from 'react';
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_PREVIEW_TEXT,
  useFontContext,
} from '../Context/mmfontContext';
import { fontStyleCategories, getStyleAllowedNames, normalizeFontName } from '../data/styleCategories';
import { allFonts, fontCatalog, fontLanguageCategories, FontDefinition } from '../data/fontCatalog';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const PANGRAMS = [
  {
    label: 'Myanmar',
    text: 'သီဟိုဠ်မှ ဉာဏ်ကြီးရှင်သည် အာယုဝဍ္ဎနဆေးညွှန်းစာကို ဇလွန်ဈေးဘေး ဗာဒံပင်ထက် အဓိဋ္ဌာန်လျက် ဂဃနဏဖတ်ခဲ့သည်',
  },
  {
    label: 'Shan',
    text: 'ပေႃးၶႂ်ႈၵျၶႅၼ်ႇၵႂႃႇၸိုင် တေလႆႈႁွင်ႉသြႃႇလီရဵၼ်းႁူႉသွၼ်ၽိုၵ်း\n\nၺႃးမိူဝ်ႈၾႃႉပၢင်ႇ ၸၢႆးသၢင်ႇလႄႈ ယိင်းဢိူၺ်ႉပေၼိုၵ်း\n\nသိူဝ်းၵျေႃႇတိုၵ်းထႃႈ ၸံလႄႈတင်းၵႆၼၼ်ႉသႃႊ။',
  },
  {
    label: 'Mon',
    text: 'ဍုၚ်ဗၟာဏံ မၞိဟ်တအ် ကောန်ဂကောံ ပၠောပ်ဗှ်ထောံ ကျာ်ဇၞော် ဂဥုဲ ဆုဒြဗ် ဖျုန် ညးသ္ကံ ဗ္တောန်သာ်ဏံ ယိုက်ဂၠေၚ်ပ္ကဴ ဇြဟတ် ဒ္ကေဝ်ဒ္ကေဝ်ၚ်၊ ဃောညးပဏ္ဍိတ ကေုာံ ခမဳတအ် လုပ်ဈာန် ပဋိပတ်ဓဝ် ဂါဎ နွံပ္ဍဲဘာဌာန်ဝေဠုဝန်ဂှ် ၜိုတ်ၝောံဇၛောဝ် လေဝ် ဆောမံၚ် ရံၚ်စိုပ်ကၠုၚ်ရ။',
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const {
    fontSize,
    changeFontSize,
    lineHeight,
    changeLineHeight,
    previewText,
    changePreviewText,
    grid,
    toggleGrid,
    selectedStyleCategory,
    changeSelectedStyleCategory,
    selectedAuthorCategory,
    changeSelectedAuthorCategory,
    selectedLanguageCategory,
    changeSelectedLanguageCategory,
    changeSearchTerm,
  } = useFontContext();

  // Build the set of font-name keys allowed by the currently selected style filter.
  // When no style is selected, null means "no restriction".
  const styleAllowedNames = getStyleAllowedNames(selectedStyleCategory);

  // Per-axis predicates. Each count below respects the OTHER two active filters,
  // so the numbers always reflect what's actually shown.
  const matchesStyle = (f: FontDefinition) =>
    !styleAllowedNames || styleAllowedNames.has(normalizeFontName(f.displayName));
  const matchesLanguage = (f: FontDefinition) =>
    selectedLanguageCategory === 'all' || f.languages.includes(selectedLanguageCategory);

  // Fonts visible under the currently selected author filter (used to narrow style/language counts).
  const authorScopedFonts = (selectedAuthorCategory && selectedAuthorCategory !== 'all')
    ? fontCatalog.find(c => c.key === selectedAuthorCategory)?.fonts ?? []
    : allFonts;

  // Author row count — respects the active style + language filters.
  const getAuthorCount = (authorFonts: FontDefinition[]) =>
    authorFonts.filter(f => matchesStyle(f) && matchesLanguage(f)).length;

  // "All Authors" count — fonts matching the active style + language filters.
  const allAuthorsCount = fontCatalog.reduce((sum, cat) => sum + getAuthorCount(cat.fonts), 0);

  // Style row count — respects the active author + language filters.
  const getCategoryCount = (categoryFonts: string[]) => {
    const allowed = new Set(categoryFonts.map(normalizeFontName));
    return authorScopedFonts.filter(f =>
      allowed.has(normalizeFontName(f.displayName)) && matchesLanguage(f)
    ).length;
  };

  // "All Fonts" count — fonts within the active author + language filters.
  const allStyleCount = authorScopedFonts.filter(matchesLanguage).length;

  // Language row count — respects the active author + style filters.
  const getLanguageCount = (languageId: string) =>
    authorScopedFonts.filter(f => matchesStyle(f) && f.languages.includes(languageId)).length;

  // "All Languages" count — fonts within the active author + style filters.
  const allLanguageCount = authorScopedFonts.filter(matchesStyle).length;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-80 bg-white border-r border-gray-200 z-50
          transform transition-transform duration-300 ease-in-out
          lg:sticky lg:top-[57px] lg:h-[calc(100vh-57px)] lg:translate-x-0 lg:z-0 lg:w-72 lg:shrink-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full overflow-y-auto scrollbar-thin">
          {/* Mobile header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 lg:hidden">
            <span className="font-semibold text-gray-900">Settings</span>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4 space-y-5">
            {/* Preview Text */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Preview Text
              </label>
              <textarea
                rows={5}
                value={previewText}
                onChange={e => changePreviewText(e.target.value)}
                placeholder="Type Myanmar text to preview..."
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none transition-all"
              />
              <div className="mt-2">
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Load Pangram
                </span>
                <div className="grid grid-cols-2 gap-2">
                  {PANGRAMS.map(p => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => changePreviewText(p.text)}
                      className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Font Size Slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Font Size
                </label>
                <span className="text-sm font-mono font-bold text-black bg-gray-100 px-2 py-0.5 rounded-md">
                  {fontSize}px
                </span>
              </div>
              <input
                type="range"
                min="10"
                max="32"
                value={fontSize}
                onChange={e => changeFontSize(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-black"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-400">10px</span>
                <span className="text-xs text-gray-400">32px</span>
              </div>
            </div>

            {/* Line Height Slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Line Height
                </label>
                <span className="text-sm font-mono font-bold text-black bg-gray-100 px-2 py-0.5 rounded-md">
                  {lineHeight}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="3"
                step={0.1}
                value={lineHeight}
                onChange={e => changeLineHeight(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-black"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-400">1</span>
                <span className="text-xs text-gray-400">3</span>
              </div>
            </div>

            {/* View Toggle */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                View Mode
              </label>
              <div className="flex rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleGrid(false)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors ${!grid
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  List
                </button>
                <button
                  onClick={() => toggleGrid(true)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors ${grid
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                  Grid
                </button>
              </div>
            </div>

            {/* Author Categories */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Author
              </label>
              <nav className="space-y-1">
                <CategoryItem
                  label="All Authors"
                  count={allAuthorsCount}
                  active={selectedAuthorCategory === 'all' || !selectedAuthorCategory}
                  onClick={() => {
                    changeSelectedAuthorCategory('all');
                    changeSearchTerm('');
                  }}
                />
                {fontCatalog.map(cat => (
                  <CategoryItem
                    key={cat.key}
                    label={cat.title}
                    count={getAuthorCount(cat.fonts)}
                    active={selectedAuthorCategory === cat.key}
                    onClick={() => {
                      changeSelectedAuthorCategory(cat.key);
                      changeSearchTerm('');
                    }}
                  />
                ))}
              </nav>
            </div>

            {/* Style Categories */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Style Category
              </label>
              <nav className="space-y-1">
                <CategoryItem
                  label="All Fonts"
                  count={allStyleCount}
                  active={selectedStyleCategory === 'all'}
                  onClick={() => {
                    changeSelectedStyleCategory('all');
                    changeSearchTerm('');
                  }}
                />
                {fontStyleCategories.map(cat => (
                  <CategoryItem
                    key={cat.name}
                    label={cat.name}
                    count={getCategoryCount(cat.fonts)}
                    active={selectedStyleCategory === cat.name}
                    onClick={() => {
                      changeSelectedStyleCategory(cat.name);
                      changeSearchTerm('');
                    }}
                  />
                ))}
              </nav>
            </div>

            {/* Language Categories */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Language
              </label>
              <nav className="space-y-1">
                <CategoryItem
                  label="All Languages"
                  count={allLanguageCount}
                  active={selectedLanguageCategory === 'all'}
                  onClick={() => {
                    changeSelectedLanguageCategory('all');
                    changeSearchTerm('');
                  }}
                />
                {fontLanguageCategories.map(lang => (
                  <CategoryItem
                    key={lang.id}
                    label={lang.name}
                    count={getLanguageCount(lang.id)}
                    active={selectedLanguageCategory === lang.id}
                    onClick={() => {
                      changeSelectedLanguageCategory(lang.id);
                      changeSearchTerm('');
                    }}
                  />
                ))}
              </nav>
            </div>

            {/* Reset button */}
            <button
              onClick={() => {
                changeFontSize(DEFAULT_FONT_SIZE);
                changeLineHeight(DEFAULT_LINE_HEIGHT);
                changePreviewText(DEFAULT_PREVIEW_TEXT);
                toggleGrid(false);
                changeSelectedAuthorCategory('all');
                changeSelectedStyleCategory('all');
                changeSelectedLanguageCategory('all');
              }}
              className="w-full py-2 px-3 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset to Default
            </button>
          </div>

          {/* Footer info */}
          <div className="mt-auto p-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center">
              Myanmar Unicode Fonts Preview
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

const CategoryItem: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`
      w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-150
      ${active
        ? 'bg-gray-100 text-black font-semibold'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }
    `}
  >
    <span className="truncate text-left">{label}</span>
    <span className={`
      shrink-0 ml-2 text-xs px-1.5 py-0.5 rounded-full font-semibold
      ${active
        ? 'bg-black text-white'
        : 'bg-gray-100 text-gray-500'
      }
    `}>
      {count}
    </span>
  </button>
);
